// src/components/loginpage/signupbox/Signupbox.jsx
// Your file. Two changes only, both marked CHANGED:
//   1. country options read "AE · United Arab Emirates" — the emoji flag is
//      gone (Windows Chrome cannot render regional-indicator glyphs, so it
//      showed as nothing), and countries without a flag are no longer filtered out
//   2. the Forgot password row is removed — it belongs on the login face only
import React, { useEffect, useState } from 'react';
import './Signupbox.css';
import warningIcon from './res/warning.png';
import approvedIcon from './res/approved.png';
import { runSignupValidation, PASSWORD_RULES, isPasswordValid } from './signupvalidator';
import { API_ENDPOINTS } from '../../../config/apiConfig';


// Map backend signup error responses to friendly, field-aware messages.
// Backend returns { status: "error", reason: "<code>" } on 422 (see contract).
const mapSignupError = (data, resp) => {
  const reason = data && (data.reason || data.error || data.message);

  if (typeof reason === 'string') {
    // Prefix-based reasons
    if (reason.startsWith('missing_fields')) {
      return 'Please fill out all fields and try again.';
    }
    if (reason.startsWith('invalid_or_empty_susenf')) return 'First name is required.';
    if (reason.startsWith('invalid_or_empty_susenl')) return 'Last name is required.';
    if (reason.startsWith('invalid_or_empty_susern')) return 'Username is required.';
    if (reason.startsWith('invalid_or_empty_suserpass')) return 'Password is required.';
    if (reason.startsWith('invalid_or_empty_suseremail')) return 'Email address is required.';
    if (reason.startsWith('invalid_or_empty_scountry')) return 'Country is required.';
    if (reason.startsWith('invalid_or_empty_apptype')) return 'Something went wrong, please try again.';

    // Exact reasons
    switch (reason) {
      case 'password_too_short':
        return 'Password must be at least 12 characters.';
      case 'password_too_long':
        return 'Password must be 20 characters or fewer.';
      case 'password_needs_uppercase':
        return 'Password must contain at least 1 uppercase letter.';
      case 'password_needs_lowercase':
        return 'Password must contain at least 1 lowercase letter.';
      case 'password_needs_number':
        return 'Password must contain at least 1 number.';
      case 'password_needs_special':
        return 'Password must contain at least 1 special character.';
      case 'invalid_email_format':
        return 'Please enter a valid email address.';
      case 'duplicate_username':
        return 'That username is already registered.';
      case 'duplicate_email':
        return 'That email is already registered.';
      case 'duplicate_found':
        return 'Those details are already registered.';
      case 'invalid_json':
        return 'Something went wrong, please try again.';
      case 'internal_error':
      case 'db_error':
        return 'Server error. Please try again shortly.';
      default:
        break;
    }

    // "<field>_too_long" family
    if (reason.endsWith('_too_long')) {
      return 'One of the values you entered is too long.';
    }

    // Unknown reason string — surface it rather than hide it.
    return `Signup failed: ${reason}`;
  }

  // No usable reason in the body
  if (resp && !resp.ok) return `Signup failed with status ${resp.status}`;
  return 'Signup failed. Unexpected server response.';
};


// `apptype` routes the emailed links: "kuestion" sends verification, reset and
// welcome links back under the Kuestion mount point (Separation contract v2.1 §6).
const Signupbox = ({ onNotify, onSignupSuccess, apptype = 'logincentral' }) => {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [isUsernameApproved, setIsUsernameApproved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Password is a controlled field so the live checklist can react per keystroke.
  const [password, setPassword] = useState('');
  const [passwordFocused, setPasswordFocused] = useState(false);


  useEffect(() => {
    const fallback = [
      { name: 'United Arab Emirates', code: 'AE' },
      { name: 'Pakistan', code: 'PK' },
      { name: 'United States', code: 'US' },
      { name: 'India', code: 'IN' },
      { name: 'United Kingdom', code: 'GB' },
    ];
    // CHANGED — flag dropped from the query and from the filter, so every
    // country the API returns makes it into the list
    fetch('https://restcountries.com/v3.1/all?fields=name,cca2')
      .then(res => res.json())
      .then(data => {
        const list = (Array.isArray(data) ? data : [])
          .map(c => ({ name: c?.name?.common || '', code: (c?.cca2 || '').toUpperCase() }))
          .filter(c => c.name && c.code)
          .sort((a, b) => a.name.localeCompare(b.name));
        setCountries(list.length ? list : fallback);
      })
      .catch(() => setCountries(fallback));
  }, []);

  const handleUsernameInput = (e) => {
    const v = e.target.value.trim();
    setIsUsernameApproved(v.length > 0);
  };

  const notifyMany = (msgs, type = 'warn', stepMs = 10) => {
    if (!onNotify) return;
    msgs.forEach((m, i) => {
      setTimeout(() => onNotify(m, type), i * stepMs); // tiny stagger defeats batching
    });
  };

  const handleSignupClick = async () => {
    if (isSubmitting) return; // prevent double click

    // 1) Frontend validation
    const { ok, errors } = runSignupValidation();
    if (!ok) {
      notifyMany(errors, 'warn');
      return;
    }

    // 2) Collect form values from the DOM
    const first    = document.getElementById('signup-first')?.value.trim() || '';
    const second   = document.getElementById('signup-second')?.value.trim() || '';
    const address  = document.getElementById('signup-address')?.value.trim() || '';
    const username = document.getElementById('signup-username')?.value.trim() || '';
    const email    = document.getElementById('signup-email')?.value.trim() || '';
    const pass     = document.getElementById('signup-password')?.value || '';
    const country  = document.getElementById('signup-country')?.value || selectedCountry || '';

    // 3) Build JSON payload that backend will receive (aligned with apirouter.py)
    const payload = {
      susenf:     first,          // first name
      susenl:     second,         // last name
      susern:     username,       // username
      suserpass:  pass,           // password
      suseremail: email,          // email
      scountry:   country,        // e.g. "AE"
      apptype, // set by the door this form sits behind
    };

    try {
      setIsSubmitting(true);

      // 4) Send POST to /api/signup via API_ENDPOINTS.SIGNUP
      const resp = await fetch(API_ENDPOINTS.SIGNUP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      let data = null;
      try {
        data = await resp.json();
      } catch {
        // response might not be JSON; ignore parse error
      }

      // Treat success based on JSON: { "status": "success", ... }
      // Any other status or missing data = failure.
      if (!data || data.status !== 'success') {
        const msg = mapSignupError(data, resp);
        onNotify && onNotify(msg, 'error');
        return;
      }

      // ✅ Backend says { status: "success", ... }
      // Instead of toast, tell parent to show RegSuccess screen.
      if (typeof onSignupSuccess === 'function') {
        onSignupSuccess();
      }

    } catch (err) {
      console.error('Signup error:', err);
      onNotify && onNotify('Network or server error during signup.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Password field is valid overall (drives the right-side icon).
  const passwordOk = isPasswordValid(password);


  return (
    <div className="signupbox">
      {/* First Name */}
      <div className="signup-row signup-item">
        <label htmlFor="signup-first">First Name</label>
      </div>
      <div className="signup-row signup-item">
        <div className="input-wrapper-auto">
          <div className="left-part">
            <input id="signup-first" type="text" placeholder="Your first name" />
          </div>
        </div>
      </div>

      {/* Second Name */}
      <div className="signup-row signup-item">
        <label htmlFor="signup-second">Second Name</label>
      </div>
      <div className="signup-row signup-item">
        <div className="input-wrapper-auto">
          <div className="left-part">
            <input id="signup-second" type="text" placeholder="Your second name" />
          </div>
        </div>
      </div>

      {/* Country + Address */}
      <div className="signup-row signup-item">
        <label htmlFor="signup-address">Country &amp; Address</label>
      </div>
      <div className="signup-row signup-item">
        <div className="input-wrapper-auto">
          <div className="countryselector">
            <select
              id="signup-country"
              aria-label="Country"
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
            >
              <option value="" disabled>Select country</option>
              {/* CHANGED — code · name, no emoji flag */}
              {countries.map(c => (
                <option key={c.code} value={c.code}>
                  {`${c.code} · ${c.name}`}
                </option>
              ))}
            </select>
          </div>
          <div className="addressinput">
            <input id="signup-address" type="text" placeholder="Your complete address" maxLength={50} />
          </div>
        </div>
      </div>

      {/* Username */}
      <div className="signup-row signup-item">
        <label htmlFor="signup-username">Username</label>
      </div>
      <div className="signup-row signup-item">
        <div className="input-wrapper">
          <div className="left-part">
            <input id="signup-username" type="text" placeholder="Choose a username" onInput={handleUsernameInput} />
          </div>
          <div className="right-part">
            <img src={isUsernameApproved ? approvedIcon : warningIcon} alt={isUsernameApproved ? 'approved' : 'warning'} className="warning-icon" />
          </div>
        </div>
      </div>

      {/* Email Address */}
      <div className="signup-row signup-item">
        <label htmlFor="signup-email">Email Address</label>
      </div>
      <div className="signup-row signup-item">
        <div className="input-wrapper">
          <div className="left-part">
            <input id="signup-email" type="email" placeholder="Your email address" />
          </div>
          <div className="right-part">
            <img src={warningIcon} alt="warning" className="warning-icon" />
          </div>
        </div>
      </div>

      {/* Password */}
      <div className="signup-row signup-item">
        <label htmlFor="signup-password">Password</label>
      </div>
      <div className="signup-row signup-item">
        {/* Wrapper is position:relative so the checklist can float below it */}
        <div className="input-wrapper password-wrapper">
          <div className="left-part">
            <input
              id="signup-password"
              type="password"
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
            />
          </div>
          <div className="right-part">
            <img
              src={passwordOk ? approvedIcon : warningIcon}
              alt={passwordOk ? 'approved' : 'warning'}
              className="warning-icon"
            />
          </div>

          {/* Live checklist — visible only while the field is focused */}
          {passwordFocused && (
            <ul className="password-checklist" aria-live="polite">
              {PASSWORD_RULES.map((rule) => {
                const met = rule.test(password);
                return (
                  <li
                    key={rule.id}
                    className={met ? 'pw-rule met' : 'pw-rule'}
                  >
                    <span className="pw-rule-mark" aria-hidden="true">
                      {met ? '✓' : '○'}
                    </span>
                    <span className="pw-rule-text">{rule.label}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Signup Button */}
      <div className="signup-row signup-item">
        <button
          className="signup-button"
          type="button"
          onClick={handleSignupClick}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Signing up...' : 'Sign up'}
        </button>
      </div>

      {/* CHANGED — Forgot password row removed; it lives on the login face */}
    </div>
  );
};

export default Signupbox;
